import React, { useMemo, useState } from "react";
import AuthNavbar from "../components/AuthNavbar";
import Sidebar from "../components/Sidebar";
import AuthFooter from "../components/AuthFooter";
import { Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import baseAPI from "../api/baseApi";
import { CreateAdditionalDetailSave } from "../redux/slices/CommonSlice";
import { useDispatch } from "react-redux";
import LogoImg from "../assets/images/Main-Logo.png";

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const RequiredInfoPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [submitting, setSubmitting] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(0);

    const requestId = useMemo(() => searchParams.get("id") || "", [searchParams]);

    const validationSchema = Yup.object({
        req_info_details: Yup.string()
            .trim()
            .min(10, t("Please enter at least 10 characters"))
            .required(t("Required Information Details is required")),
        req_documents: Yup.array()
            .of(Yup.mixed())
            .min(1, t("Please upload at least one file"))
            .test("file-size", t("Each file must be 10MB or less"), (files) => {
                if (!files || !files.length) return false;
                return files.every((file) => file && file.size <= MAX_FILE_SIZE);
            }),
    });

    const formik = useFormik({
        initialValues: {
            id: requestId,
            req_info_details: "",
            req_documents: [],
        },
        enableReinitialize: true,
        validationSchema,
        onSubmit: async (values) => {
            if (!requestId) {
                toast.error(t("Missing request id"));
                return;
            }

            try {
                setSubmitting(true);
                const res = await dispatch(CreateAdditionalDetailSave(values));
                if (res?.payload) {
                    toast.success(t("Required Information Saved Successfully"));
                    navigate("/");
                }
            } catch (error) {
                console.log("ERRR", error);
                toast.error(t("Something Went Worng !"));
            } finally {
                setSubmitting(false);
            }
        }
    });

    const handleFileChange = (event) => {
        const { files } = event.currentTarget;
        if (!files) return;

        const fileArray = Array.from(files);
        const acceptedExtensions = [".png", ".jpg", ".jpeg", ".pdf", ".doc", ".docx"];

        const validFiles = [];
        for (let i = 0; i < fileArray.length; i++) {
            const file = fileArray[i];
            const hasValidExtension = acceptedExtensions.some((ext) =>
                file.name.toLowerCase().endsWith(ext)
            );

            if (!hasValidExtension) {
                toast.error(t("Invalid file type. Supported: .png, .jpg, .pdf, .doc"));
                continue;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast.error(t("Each file must be 10MB or less"));
                continue;
            }

            validFiles.push(file);
        }

        const currentFiles = formik.values.req_documents || [];
        formik.setFieldValue("req_documents", [...currentFiles, ...validFiles]);

        // Reset the native file input so its built-in file count text doesn't get out of sync
        setFileInputKey((prev) => prev + 1);
    };

    const handleRemoveFile = (index) => {
        const updatedFiles = (formik.values.req_documents || []).filter((_, i) => i !== index);
        formik.setFieldValue("req_documents", updatedFiles);

        // Also reset the native file input so it no longer shows the old file count
        setFileInputKey((prev) => prev + 1);
    };

    const onCancel = () => {
        navigate(-1);
    };

    const detailsError = formik.touched.req_info_details && formik.errors.req_info_details;
    const filesError = formik.touched.req_documents && formik.errors.req_documents;

    return (
        <>
            <div className="dashboard-content cust-height mt-4">
                <div className="container">
                    <div className="d-flex justify-content-center mb-3">
                        <button
                            type="button"
                            className="btn p-0 border-0 bg-transparent"
                            onClick={() => {
                                const baseUrl = process.env.REACT_APP_BASE_URL || "/";
                                window.location.href = baseUrl;
                            }}
                        >
                            <img
                                src={LogoImg}
                                alt="Logo"
                                style={{ maxHeight: "60px", width: "auto" }}
                            />
                        </button>
                    </div>
                    <div className="row">
                        <div className="col-sm-2 dashboard-left">
                            {/* <Sidebar /> */}
                        </div>
                        <div className="col-sm-8">
                            <div className="col-12">
                                <div className="dashboard-card">
                                    <div className="dashboard-card-header mb-4">
                                        <div className="d-flex justify-content-between align-items-center w-100">
                                            <h3>{t("Required Information")}</h3>
                                        </div>
                                    </div>
                                    <div className="dashboard-card-body">
                                        <Form
                                            className="mt-3"
                                            onSubmit={formik.handleSubmit}
                                            encType="multipart/form-data"
                                        >
                                            <div className="mb-3">
                                                <label
                                                    htmlFor="req_info_details"
                                                    className="form-label"
                                                >
                                                    {t("Required Information Details")} <span style={{ color: "red" }}>*</span>
                                                </label>
                                                <textarea
                                                    id="req_info_details"
                                                    name="req_info_details"
                                                    className="form-control"
                                                    rows="4"
                                                    placeholder={t("Enter required information details")}
                                                    value={formik.values.req_info_details}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    aria-required="true"
                                                    aria-invalid={detailsError ? "true" : "false"}
                                                    aria-describedby={detailsError ? "details-error" : undefined}
                                                />
                                                {detailsError && (
                                                    <div id="details-error" className="text-danger mt-1">
                                                        {formik.errors.req_info_details}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <label
                                                    htmlFor="req_documents"
                                                    className="form-label"
                                                >
                                                    {t("Attachments")} <span style={{ color: "red" }}>*</span>
                                                </label>
                                                <input
                                                    key={fileInputKey}
                                                    id="req_documents"
                                                    name="req_documents"
                                                    type="file"
                                                    className="form-control"
                                                    multiple
                                                    onChange={handleFileChange}
                                                    onBlur={() => formik.setFieldTouched("req_documents", true)}
                                                    aria-required="true"
                                                    aria-invalid={filesError ? "true" : "false"}
                                                    aria-describedby={filesError ? "files-error" : undefined}
                                                    accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                                                />
                                                {filesError && (
                                                    <div id="files-error" className="text-danger mt-1">
                                                        {formik.errors.req_documents}
                                                    </div>
                                                )}

                                                {formik.values.req_documents && formik.values.req_documents.length > 0 && (
                                                    <div className="mt-2">
                                                        <ul className="list-unstyled mb-0">
                                                            {formik.values.req_documents.map((file, index) => (
                                                                <li
                                                                    key={index}
                                                                    className="d-flex justify-content-between align-items-start mb-1"
                                                                >
                                                                    <div style={{ maxWidth: "70%", wordBreak: "break-all", whiteSpace: "normal" }}>
                                                                        <span className="fw-semibold">{file.name}</span>{" "}
                                                                        <span className="text-muted">
                                                                            ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => handleRemoveFile(index)}
                                                                    >
                                                                        {t("Remove")}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="d-flex justify-content-end gap-2 mt-4">
                                                {/* <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    onClick={onCancel}
                                                >
                                                    {t("Cancel")}
                                                </button> */}
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={submitting}
                                                >
                                                    {submitting ? t("Loading...") : t("Submit")}
                                                </button>
                                            </div>
                                        </Form>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-2 dashboard-left">
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RequiredInfoPage;
